/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Upload, Trash2, Copy, Image, FolderOpen, FolderPlus, Folder, MoveRight, FileText, FileSpreadsheet, File, CheckSquare, X, Search } from 'lucide-react';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const FOLDER_LABELS = {
  'generale': 'Generale', 'dottori': 'Dottori', 'eventi': 'Eventi',
  'news': 'News', 'studio': 'Studio', 'sito': 'Sito',
};

const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

function getFileIcon(name) {
  const ext = (name || '').toLowerCase().split('.').pop();
  if (['pdf'].includes(ext)) return <FileText size={32} className="text-red-500" />;
  if (['doc', 'docx'].includes(ext)) return <FileText size={32} className="text-blue-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet size={32} className="text-green-600" />;
  if (['ppt', 'pptx'].includes(ext)) return <FileText size={32} className="text-orange-500" />;
  if (['txt'].includes(ext)) return <File size={32} className="text-gray-500" />;
  return null; // It's an image
}

function isImage(name) {
  const ext = '.' + (name || '').toLowerCase().split('.').pop();
  return IMAGE_EXTS.includes(ext);
}

export default function GestioneMedia() {
  const { user } = useAuth();

  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [moveDialog, setMoveDialog] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, fRes] = await Promise.all([
        axios.get(`${API}/media`, { params: { folder: selectedFolder || undefined } }),
        axios.get(`${API}/media/folders`, {})
      ]);
      setMedia(mRes.data);
      setFolders(fRes.data);
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, [selectedFolder]);

  useEffect(() => { fetchData(); setSelectedIds([]); }, [fetchData]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    const folder = selectedFolder || 'generale';
    try {
      const res = await axios.post(`${API}/media/upload-multiple?folder=${encodeURIComponent(folder)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { uploaded, errors } = res.data;
      if (uploaded > 0) toast.success(`${uploaded} file caricati in "${FOLDER_LABELS[folder] || folder}"`);
      if (errors.length > 0) toast.error(`Errori: ${errors.join(', ')}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleBulkDelete = async () => {
    try {
      const res = await axios.post(`${API}/media/bulk-delete`, selectedIds, {});
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const handleBulkMove = async () => {
    if (!moveTarget) return;
    try {
      const res = await axios.put(`${API}/media/bulk-move?target_folder=${encodeURIComponent(moveTarget)}`, selectedIds, {});
      toast.success(res.data.message);
      setSelectedIds([]);
      setMoveDialog(false);
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/media/${id}`, {}); toast.success('Eliminato'); fetchData(); }
    catch { toast.error('Errore'); }
  };

  const copyUrl = (url) => {
    const fullUrl = window.location.origin + url;
    navigator.clipboard.writeText(fullUrl).then(() => toast.success('URL copiato')).catch(() => toast.info(`URL: ${fullUrl}`));
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedIds(prev => prev.length === visibleMedia.length && visibleMedia.length > 0 ? [] : visibleMedia.map(m => m.id));

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/media/folders?name=${encodeURIComponent(newFolderName)}`, {}, {});
      toast.success(`Cartella "${res.data.name}" creata`);
      setNewFolderDialog(false); setNewFolderName(''); setSelectedFolder(res.data.name); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.length) handleUpload(e.dataTransfer.files); };

  const totalCount = folders.reduce((sum, f) => sum + f.count, 0);

  // Ricerca per nome file (client-side, sulla cartella corrente)
  const q = search.trim().toLowerCase();
  const visibleMedia = q
    ? media.filter(m => (m.original_name || '').toLowerCase().includes(q) || (m.filename || '').toLowerCase().includes(q))
    : media;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <PageTitle title="Media Library" info="Immagini e documenti del sito organizzati in cartelle. Trascina i file per caricarli, usa la ricerca per nome file." extra={<span className="text-sm text-muted-foreground font-normal" data-testid="media-count">{selectedFolder ? `${FOLDER_LABELS[selectedFolder] || selectedFolder} · ` : ''}{q ? `${visibleMedia.length} di ${media.length}` : media.length} file</span>} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setNewFolderDialog(true)}><FolderPlus size={16} className="mr-1" /> Nuova Cartella</Button>
          <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" className="hidden" onChange={e => handleUpload(e.target.files)} />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
            <Upload size={16} className="mr-1" /> {uploading ? 'Caricamento...' : 'Carica File'}
          </Button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-[hsl(var(--accent))] rounded-lg px-4 py-3 mb-4">
          <CheckSquare size={16} className="text-[#238A92]" />
          <span className="text-sm font-medium">{selectedIds.length} selezionati</span>
          <Button size="sm" variant="outline" className="h-7" onClick={() => { setMoveTarget(''); setMoveDialog(true); }}>
            <MoveRight size={12} className="mr-1" /> Sposta
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 size={12} className="mr-1" /> Elimina
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Elimina {selectedIds.length} file</AlertDialogTitle>
                <AlertDialogDescription>Sei sicuro di voler eliminare {selectedIds.length} file? Questa azione non può essere annullata.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600">Elimina {selectedIds.length} file</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setSelectedIds([])}>
            <X size={12} className="mr-1" /> Deseleziona
          </Button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Folder sidebar */}
        <div className="w-48 flex-shrink-0">
          <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Cartelle</p>
          <div className="space-y-1">
            <button onClick={() => setSelectedFolder(null)} className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${!selectedFolder ? 'bg-[#238A92] text-white' : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]'}`}>
              <FolderOpen size={16} /> Tutti ({totalCount})
            </button>
            {folders.map(f => (
              <button key={f.name} onClick={() => setSelectedFolder(f.name)} className={`w-full text-left flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${selectedFolder === f.name ? 'bg-[#238A92] text-white' : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]'}`}>
                <span className="flex items-center gap-2 truncate"><Folder size={16} /> {FOLDER_LABELS[f.name] || f.name}</span>
                <span className={`text-xs ${selectedFolder === f.name ? 'text-white/70' : 'text-muted-foreground'}`}>{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-5 mb-4 text-center transition-colors cursor-pointer ${dragActive ? 'border-[#238A92] bg-[#238A92]/5' : 'border-[hsl(var(--border))] hover:border-[#238A92]/50'}`}
            onDragEnter={handleDragIn} onDragLeave={handleDragOut} onDragOver={handleDrag} onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={24} className={`mx-auto mb-1 ${dragActive ? 'text-[#238A92]' : 'text-muted-foreground'}`} />
            <p className="text-sm font-medium text-[#1F2A37]">{uploading ? 'Caricamento...' : `Trascina i file in "${FOLDER_LABELS[selectedFolder || 'generale'] || selectedFolder || 'Generale'}"`}</p>
            <p className="text-xs text-muted-foreground">Immagini, PDF, Word, Excel, PowerPoint — Max 10MB</p>
          </div>

          {/* Ricerca + Seleziona tutti */}
          {(media.length > 0 || q) && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
              <div className="relative flex-1 max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={`Cerca per nome file${selectedFolder ? ` in "${FOLDER_LABELS[selectedFolder] || selectedFolder}"` : ''}...`}
                  className="pl-8 pr-8 h-9"
                  data-testid="media-search-input"
                />
                {search && (
                  <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37]" aria-label="Pulisci ricerca" data-testid="media-search-clear">
                    <X size={14} />
                  </button>
                )}
              </div>
              {visibleMedia.length > 0 && (
                <div className="flex items-center gap-2">
                  <Checkbox checked={selectedIds.length === visibleMedia.length && visibleMedia.length > 0} onCheckedChange={selectAll} data-testid="media-select-all" />
                  <span className="text-xs text-muted-foreground">Seleziona tutti {q ? `(${visibleMedia.length} trovati)` : ''}</span>
                </div>
              )}
            </div>
          )}

          {/* Media grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="aspect-square rounded-xl" />)}
            </div>
          ) : visibleMedia.length === 0 ? (
            <Card><CardContent className="py-12 text-center" data-testid="media-empty">
              {q ? <Search size={40} className="mx-auto text-muted-foreground mb-3" /> : <Image size={40} className="mx-auto text-muted-foreground mb-3" />}
              <p className="font-medium text-[#1F2A37] mb-1" style={{fontFamily:'Space Grotesk'}}>{q ? 'Nessun risultato' : 'Nessun file'}</p>
              <p className="text-sm text-muted-foreground">{q ? `Nessun file contiene "${search.trim()}"` : selectedFolder ? 'Cartella vuota' : 'Carica il primo file'}</p>
              {q && <Button variant="outline" size="sm" className="mt-3" onClick={() => setSearch('')}>Azzera ricerca</Button>}
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {visibleMedia.map(m => {
                const selected = selectedIds.includes(m.id);
                const fileIcon = getFileIcon(m.original_name);
                return (
                  <Card key={m.id} className={`overflow-hidden group transition-all duration-200 ${selected ? 'ring-2 ring-[#238A92] shadow-[var(--shadow-md)]' : 'hover:shadow-[var(--shadow-md)]'}`}>
                    <div className="aspect-square overflow-hidden bg-[hsl(var(--muted))] relative">
                      {/* Selection checkbox */}
                      <div className={`absolute top-2 left-2 z-10 transition-opacity ${selected || 'opacity-0 group-hover:opacity-100'}`}>
                        <Checkbox checked={selected} onCheckedChange={() => toggleSelect(m.id)} className="bg-white shadow-sm" />
                      </div>
                      {/* Content: image or file icon */}
                      {fileIcon ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-white">
                          {fileIcon}
                          <p className="text-[10px] text-muted-foreground mt-1 uppercase">{(m.original_name || '').split('.').pop()}</p>
                        </div>
                      ) : (
                        <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" loading="lazy" />
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
                        <Button size="sm" variant="secondary" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); copyUrl(m.url); }}><Copy size={14} /></Button>
                        <Button size="sm" variant="destructive" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                    <CardContent className="p-2">
                      <p className="text-xs font-medium truncate" title={m.original_name}>{m.original_name}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-muted-foreground">{formatSize(m.size)}</p>
                        {!selectedFolder && m.folder && <span className="text-[10px] text-[#238A92] bg-[#238A92]/10 px-1.5 py-0.5 rounded">{FOLDER_LABELS[m.folder] || m.folder}</span>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* New Folder Dialog */}
      <Dialog open={newFolderDialog} onOpenChange={setNewFolderDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>Nuova Cartella</DialogTitle></DialogHeader>
          <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Nome cartella..." onKeyDown={e => e.key === 'Enter' && createFolder()} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewFolderDialog(false)}>Annulla</Button>
            <Button onClick={createFolder} disabled={!newFolderName.trim()} className="bg-[#238A92] hover:bg-[#0F5E66] text-white"><FolderPlus size={14} className="mr-1" /> Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveDialog} onOpenChange={setMoveDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>Sposta {selectedIds.length} file</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Seleziona la cartella di destinazione:</p>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {folders.map(f => (
                <button key={f.name} onClick={() => setMoveTarget(f.name)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors ${moveTarget === f.name ? 'bg-[#238A92] text-white' : 'hover:bg-[hsl(var(--secondary))]'}`}
                >
                  <Folder size={16} /> {FOLDER_LABELS[f.name] || f.name} ({f.count})
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMoveDialog(false)}>Annulla</Button>
            <Button onClick={handleBulkMove} disabled={!moveTarget} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
              <MoveRight size={14} className="mr-1" /> Sposta in "{FOLDER_LABELS[moveTarget] || moveTarget}"
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
